import React, { useState } from 'react';
import { Download, FileText, PhoneCall } from 'lucide-react';
import { CalcLabel, CalcInput } from './shared';
import { Textarea } from '../../ui/textarea';

export const StepDetails = ({ config, customer, setCustomer, acceptedRules, setAcceptedRules, onSubmit, isSubmitting, submitted, apiUrl, onEnquire }) => {
  const [error, setError] = useState('');
  const rules = (config.rules || []).filter(r => r.show_in_calculator !== false);
  const needsAcceptance = rules.some(r => r.mandatory_acceptance);

  if (submitted) {
    return (
      <div className="text-center py-8 space-y-4" data-testid="calc-success">
        <FileText className="w-10 h-10 text-[#C6A87C] mx-auto" strokeWidth={1.2} />
        <div>
          <p className="font-wvc-display text-2xl text-[#0A1710]">Your estimate is ready</p>
          <p className="text-sm text-[#1A3626]/60 mt-1">Estimate No. <span className="font-medium text-[#0A1710]">{submitted.estimate_number}</span> · valid until {submitted.valid_until}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={`${apiUrl}/pricing/estimates/${submitted.estimate_id}/pdf`}
            className="inline-flex items-center gap-2 bg-[#1A3626] text-white hover:bg-[#C6A87C] hover:text-[#0A1710] px-8 py-4 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid="calc-download-pdf-btn"
          >
            <Download className="w-4 h-4" /> Download PDF Estimate
          </a>
          <button
            type="button"
            onClick={onEnquire}
            className="inline-flex items-center gap-2 border border-[#1A3626] text-[#1A3626] hover:bg-[#1A3626] hover:text-white px-8 py-4 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid="calc-success-enquire-btn"
          >
            <PhoneCall className="w-4 h-4" /> Talk to WVC Team
          </button>
        </div>
        <p className="text-[11px] text-[#1A3626]/50 max-w-md mx-auto">Our team has received your estimate and will reach out shortly to help you plan further.</p>
      </div>
    );
  }

  const submit = () => {
    if (!customer.name || !customer.phone) {
      setError('Please enter your name and phone number.');
      return;
    }
    if (needsAcceptance && !acceptedRules) {
      setError('Please accept the venue rules and regulations to continue.');
      return;
    }
    setError('');
    onSubmit();
  };

  return (
    <div className="space-y-5" data-testid="calc-step-details">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <CalcLabel>Your name *</CalcLabel>
          <CalcInput value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value })} data-testid="calc-cust-name" />
        </div>
        <div>
          <CalcLabel>Mobile number *</CalcLabel>
          <CalcInput value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} data-testid="calc-cust-phone" />
        </div>
        <div>
          <CalcLabel>Email</CalcLabel>
          <CalcInput type="email" value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })} data-testid="calc-cust-email" />
        </div>
        <div>
          <CalcLabel>City</CalcLabel>
          <CalcInput value={customer.city} onChange={e => setCustomer({ ...customer, city: e.target.value })} data-testid="calc-cust-city" />
        </div>
        <div>
          <CalcLabel>Budget range</CalcLabel>
          <select
            value={customer.budget_range}
            onChange={e => setCustomer({ ...customer, budget_range: e.target.value })}
            className="w-full bg-white border border-[#E5DCCF] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C6A87C]"
            data-testid="calc-cust-budget"
          >
            <option value="">Select…</option>
            <option>Under ₹5 Lakh</option>
            <option>₹5 – 10 Lakh</option>
            <option>₹10 – 20 Lakh</option>
            <option>₹20 – 50 Lakh</option>
            <option>Above ₹50 Lakh</option>
          </select>
        </div>
        <div>
          <CalcLabel>Wedding date (if fixed)</CalcLabel>
          <CalcInput type="date" value={customer.wedding_date} onChange={e => setCustomer({ ...customer, wedding_date: e.target.value })} data-testid="calc-cust-date" />
        </div>
      </div>
      <div>
        <CalcLabel>Additional requirements</CalcLabel>
        <Textarea rows={3} value={customer.requirements} onChange={e => setCustomer({ ...customer, requirements: e.target.value })} className="rounded-none border-[#E5DCCF] focus-visible:ring-[#C6A87C]" data-testid="calc-cust-requirements" />
      </div>

      {rules.length > 0 && (
        <div className="border border-[#E5DCCF] bg-white" data-testid="calc-rules">
          <p className="px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C] border-b border-[#E5DCCF]">Venue Rules & Regulations</p>
          <div className="max-h-48 overflow-y-auto divide-y divide-[#E5DCCF]/60">
            {rules.map(r => (
              <div key={r.id} className="px-4 py-3">
                <p className="text-xs font-bold text-[#0A1710]">
                  {r.category} · {r.title}
                  {r.important && <span className="ml-2 text-[9px] uppercase text-[#C6A87C]">Important</span>}
                </p>
                <p className="text-xs text-[#1A3626]/70 mt-0.5 font-light">{r.body}</p>
              </div>
            ))}
          </div>
          {needsAcceptance && (
            <label className="flex items-start gap-2.5 px-4 py-3 border-t border-[#E5DCCF] cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedRules}
                onChange={e => setAcceptedRules(e.target.checked)}
                className="mt-0.5 accent-[#1A3626]"
                data-testid="calc-accept-rules"
              />
              <span className="text-xs text-[#1A3626]/80">I have read and accept the venue rules and regulations, and I understand this is an estimate subject to final availability and confirmation.</span>
            </label>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600" data-testid="calc-details-error">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={isSubmitting}
        className="w-full bg-[#1A3626] text-white hover:bg-[#C6A87C] hover:text-[#0A1710] py-4 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
        data-testid="calc-submit-btn"
      >
        {isSubmitting ? 'Generating your estimate…' : 'Get My Estimate & PDF'}
      </button>
    </div>
  );
};
